import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from "@/services/studio/client";
import { useSkillsRequestContext } from "@/services/skills";
import { useToast } from "@/hooks/ui/useToast";
import type {
  AgentCreateResponse,
  AgentDetailResponse,
  AgentListResponse,
  AgentPlanResponse,
  AgentPlanStep,
  AgentRunDetailResponse,
  AgentRunListResponse,
  AgentType,
} from "@/services/studio/types";
import {
  normalizeAgentDetailResponse,
  normalizeAgentListResponse,
  normalizeAgentRunDetailResponse,
  normalizeAgentRunListResponse,
} from "@/services/agents/normalize";

export function useAgentRuns(opts: { agent_id?: string; limit?: number } = {}) {
  const ctx = useSkillsRequestContext();
  const params = new URLSearchParams();
  if (opts.agent_id) params.set("agent_id", opts.agent_id);
  params.set("limit", String(opts.limit ?? 50));
  const search = params.toString();
  return useQuery<AgentRunListResponse>({
    queryKey: ["agent-runs", opts.agent_id ?? null, opts.limit ?? 50, ctx.orgId, ctx.userId],
    queryFn: async () => normalizeAgentRunListResponse(await apiGet<unknown>(`/api/agent-runs?${search}`)),
    enabled: Boolean(ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}


export function useAgents() {
  const ctx = useSkillsRequestContext();
  return useQuery<AgentListResponse>({
    queryKey: ["agents", ctx.orgId, ctx.userId],
    queryFn: async () => normalizeAgentListResponse(await apiGet<unknown>("/api/agents")),
    enabled: Boolean(ctx.orgId),
    // Mutations (create/delete/update) call invalidateQueries on success, so background
    // refetch at 30 s added no value for the sidebar count. 5 min keeps data fresh
    // without unnecessary polling across every authenticated route.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useAgent(id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<AgentDetailResponse>({
    queryKey: ["agent", id, ctx.orgId, ctx.userId],
    queryFn: async () => normalizeAgentDetailResponse(await apiGet<unknown>(`/api/agents/${encodeURIComponent(id!)}`)),
    enabled: Boolean(id && ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useAgentRun(id: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<AgentRunDetailResponse>({
    queryKey: ["agent-run", id, ctx.orgId, ctx.userId],
    queryFn: async () => normalizeAgentRunDetailResponse(await apiGet<unknown>(`/api/agent-runs/${encodeURIComponent(id!)}`)),
    enabled: Boolean(id && ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useStopAgentRun() {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (runId: string) => apiPost<AgentRunDetailResponse>(`/api/agent-runs/${encodeURIComponent(runId)}/stop`, {}),
    onSuccess: (_data, runId) => {
      void queryClient.invalidateQueries({ queryKey: ["agent-run", runId, ctx.orgId, ctx.userId] });
      void queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
    },
  });
}

export function useRerunAgentRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => apiPost<{ ok: boolean; previous_run_id: string; agent_id: string; input: string; scope: Record<string, unknown>; status: string }>(`/api/agent-runs/${encodeURIComponent(runId)}/rerun`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
    },
  });
}


export function usePlanAgent() {
  return useMutation<AgentPlanResponse, Error, { name: string; input: string; agent_type: AgentType; scope?: Record<string, string> }>({
    mutationFn: (body) => apiPost<AgentPlanResponse>("/api/agents/plan", body),
  });
}

export interface CreateAgentPayload {
  name: string;
  input: string;
  agent_type: AgentType;
  scope?: Record<string, string>;
  tools?: string[];
  plan?: AgentPlanStep[];
  config?: Record<string, unknown>;
  llm_config?: Record<string, unknown>;
  capabilities?: Array<Record<string, unknown>>;
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation<AgentCreateResponse, Error, CreateAgentPayload>({
    mutationFn: (body) => apiPost<AgentCreateResponse>("/api/agents", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
  });
}

export interface UpdateAgentPayload {
  name?: string;
  input?: string;
  agent_type?: AgentType;
  scope?: Record<string, string>;
  tools?: string[];
  plan?: AgentPlanStep[];
  config?: Record<string, unknown>;
  llm_config?: Record<string, unknown>;
}

// Edit a user-owned agent. The backend clears any crystallized reactive recipe
// so the agent's NEXT run re-discovers tools for the edited definition — the
// "edit → re-trigger tools" contract. Default/global agents are immutable (403).
export function useUpdateAgent() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation<AgentCreateResponse, Error, { agentId: string; body: UpdateAgentPayload }>({
    mutationFn: ({ agentId, body }) => apiPut<AgentCreateResponse>(`/api/agents/${encodeURIComponent(agentId)}`, body),
    onSuccess: (_data, { agentId }) => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : undefined;
      toast({
        title: "Edit failed",
        description:
          status === 403
            ? "Built-in agents can't be edited. Duplicate it to customize."
            : "Couldn't save the agent. Please try again.",
      });
    },
  });
}

// Delete a user-created agent. The backend rejects default/global agents (404)
// and agents with an in-flight run (409), so failures need user feedback —
// a silent fire-and-forget delete would just appear to do nothing.
export function useDeleteAgent() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation<void, Error, string>({
    mutationFn: (agentId) => apiDelete(`/api/agents/${encodeURIComponent(agentId)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["agent-runs"] });
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : undefined;
      const description =
        status === 409
          ? "This agent has a run in progress. Stop or wait for the run to finish, then try again."
          : status === 404
            ? "This agent can't be deleted."
            : "Couldn't delete the agent. Please try again.";
      toast({ title: "Delete failed", description });
    },
  });
}
